import React from "react";
import { Autocomplete, Box, Chip, Grid, TextField } from "@mui/material";
import clsx from "clsx";
import styles from "../../../assets/styles/pageStyles/ruleBasedInbox/inboxControls/inboxControls.module.scss";
import contactStyles from "../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";

export interface InboxFilterAutocompleteProps<T> {
    options: T[];
    value: T[];
    onChange: (event: React.SyntheticEvent, value: T[]) => void;
    getOptionLabel: (option: T) => string;
    renderOptionLabel?: (option: T) => React.ReactNode;
    isOptionEqualToValue: (option: T, value: T) => boolean;
    getOptionKey: (option: T) => string | number;
    disabled?: boolean;
    placeholder: string;
    selectedCountLabel?: string;
    suffixAction?: React.ReactNode;
    chipClassName?: string;
}

const InboxFilterAutocomplete = <T,>({
    options,
    value,
    onChange,
    getOptionLabel,
    renderOptionLabel,
    isOptionEqualToValue,
    getOptionKey,
    disabled = false,
    placeholder,
    selectedCountLabel = "item",
    suffixAction,
    chipClassName,
}: InboxFilterAutocompleteProps<T>) => {
    
    const count = value?.length ?? 0;
    const summaryText = count > 0 ? `${count} ${selectedCountLabel}${count === 1 ? "" : "s"} selected` : placeholder;

    const handleChipDelete = (optionToRemove: T) => {
        const next = value.filter(
            (option) => getOptionKey(option) !== getOptionKey(optionToRemove)
        );
        onChange({} as React.SyntheticEvent, next);
    };

    const getDisplayLabel = (option: T) =>
        renderOptionLabel ? renderOptionLabel(option) : getOptionLabel(option);

    return (
        <Grid item className={styles.ticketCreatedByWrapper}>
            <Box className={styles.inboxFilterInputRow}>
                <Box className={styles.inboxFilterAutocompleteField}>
                    <Autocomplete
                        multiple
                        disabled={disabled}
                        className={clsx(contactStyles.contactFilterDropdownsInPopup, styles.ticketCreatedByDropdown)}
                        options={options}
                        getOptionLabel={getOptionLabel}
                        value={value}
                        isOptionEqualToValue={isOptionEqualToValue}
                        onChange={onChange}
                        ListboxProps={{ className: styles.inboxFilterDropdownListbox }}
                        renderTags={() => null}
                        renderOption={(props, option) => (
                            <li {...props} key={getOptionKey(option)}>
                                {getDisplayLabel(option)}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} placeholder={summaryText} />
                        )}
                    />
                </Box>
                {suffixAction && (
                    <Box className={styles.inboxFilterSuffixAction}>
                        {suffixAction}
                    </Box>
                )}
            </Box>
            {value?.length > 0 && 
                <Box className={styles.ticketCreatedByChips}>
                    {value.map((option) => (
                        <Chip
                            key={getOptionKey(option)}
                            label={getDisplayLabel(option)}
                            onDelete={() => handleChipDelete(option)}
                            disabled={disabled}
                            className={clsx(styles.ticketCreatedByChip, chipClassName)}
                            classes={{ deleteIcon: styles.ticketCreatedByChipDeleteIcon }}
                        />
                    ))}
                </Box>
            }
        </Grid>
    );
};

export default InboxFilterAutocomplete;
